import React from 'react';
import Localize from 'react-intl-universal';

import { get } from 'lodash-es';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import {
  formatDateTimeToDMY,
  formatLongDateTimeStringToDateTimeObject,
  formatPrice,
  seatsInfo
} from '@common/Util';
import { useGlobalContext } from '@hooks/Context/GlobalContext';
import BookingDialog from '@pages/BookingProcess/SessionSelection/SessionDialog';

const StyledTypography = styled(Typography)(({ theme }) => ({
  fontSize: '12px !important',
  fontWeight: '700 !important'
}));

const StyledChip = styled(Chip)(() => ({
  height: '1.5rem !important',
  borderRadius: '5px !important',
  margin: '2px !important'
}));

const StyledCard = styled(Card)(({ theme }) => ({
  background: theme.palette.booking.list,
  marginBottom: '1rem'
}));

export const SingleSessionSubheader = ({ firstText, secondText, containerSX, subheaderSX }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      minWidth: 0,
      ...containerSX
    }}
  >
    <Tooltip title={firstText || ''} arrow>
      <Typography
        sx={{
          ...subheaderSX,
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          cursor: 'default'
        }}
      >
        {firstText}
      </Typography>
    </Tooltip>

    {secondText && (
      <Typography
        sx={{
          ...subheaderSX,
          flexShrink: 0,
          whiteSpace: 'nowrap'
        }}
      >
        | {secondText}
      </Typography>
    )}
  </Box>
);

const ListCard = ({ session, handleSession, checkValue }) => {
  const date = formatDateTimeToDMY(session?.timeBlockDate);
  const isMandatory = !session?.optionalSession;
  const trainers = session?.teamMembers
    ?.map((trainer) => trainer?.eventParticipant?.name || '')
    .join(', ');
  const venueName = get(session, 'venues[0].name', '');
  const [contextData] = useGlobalContext();

  const sessionPrice = formatPrice(session?.price);
  const sessionCurrency = session?.standardPrice?.currency?.value || '';

  return (
    <StyledCard variant='outlined'>
      <CardContent
        sx={{
          padding: 0,
          ':last-child': {
            paddingBottom: 0
          }
        }}
      >
        <Grid container spacing={2} sx={{ marginTop: 0, marginBottom: '0.3rem', padding: 0 }}>
          <Grid item xs={9} sm={10} sx={{ paddingTop: '0' }}>
            <FormGroup sx={{ paddingLeft: '10px' }}>
              <FormControlLabel
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: 18,
                    fontWeight: 700
                  }
                }}
                control={
                  <Checkbox
                    id={session?.id.toString()}
                    checked={isMandatory || checkValue(session?.id)}
                    disabled={isMandatory}
                    onChange={handleSession}
                  />
                }
                label={`${session?.name} (${isMandatory ? Localize.get('Booking.mandatory') : Localize.get('Booking.optional')})`}
              />
            </FormGroup>
            <Box
              sx={
                isMandatory
                  ? {
                      opacity: '0.6',
                      paddingLeft: '2.8rem'
                    }
                  : { opacity: '1', paddingLeft: '2.8rem' }
              }
            >
              <SingleSessionSubheader firstText={venueName} secondText={session?.sessionNumber} />
              <Box>
                <Typography display='inline'>{Localize.get('Booking.trainer') + ': '}</Typography>
                <Typography display='inline'>{trainers || ''}</Typography>
              </Box>
              <Box>
                <Typography display='inline'>{`${formatLongDateTimeStringToDateTimeObject(session?.startDateTime)} - `}</Typography>
                <Typography display='inline'>{`${formatLongDateTimeStringToDateTimeObject(session?.endDateTime)}`}</Typography>
              </Box>
              <Stack direction='row' spacing={1} sx={{ mt: '12px', mb: '12px', flexWrap: 'wrap' }}>
                <StyledChip
                  label={seatsInfo(session?.availableSeats, contextData?.isSeatsNumber)}
                />
                <StyledChip
                  label={
                    session?.hasWaitingList
                      ? Localize.get('Booking.hasWaitingList')
                      : Localize.get('Booking.noWaitingList')
                  }
                />
                {session?.optionalSession && (
                  <StyledChip
                    label={`${Localize.get('Booking.price')}: 
                    ${
                      session.price
                        ? `${sessionPrice} ${sessionCurrency} `
                        : Localize.get('CustomerLoyaltyEvents.free')
                    }`}
                  />
                )}
              </Stack>
              <>
                {session?.sessionRegistrationDeadline && (
                  <Box>
                    <StyledTypography display='inline'>
                      {`${Localize.get('Booking.bookable')}: `}
                    </StyledTypography>
                    <Typography display='inline' sx={{ fontSize: '12px' }}>
                      {formatDateTimeToDMY(session?.sessionRegistrationDeadline)}
                    </Typography>
                  </Box>
                )}
                {session?.sessionCancellationDeadline && (
                  <Box>
                    <StyledTypography display='inline'>
                      {`${Localize.get('Booking.cancelBookingUntil')}: `}
                    </StyledTypography>
                    <Typography display='inline' sx={{ fontSize: '12px' }}>
                      {formatDateTimeToDMY(session?.sessionCancellationDeadline)}
                    </Typography>
                  </Box>
                )}
              </>
            </Box>
          </Grid>
          <Grid
            item
            xs={3}
            sm={2}
            sx={{
              pt: '4px !important',
              textAlign: 'center',
              paddingLeft: { xs: '0px !important', sm: '16px' }
            }}
          >
            <BookingDialog session={session} date={date} trainers={trainers} />
          </Grid>
        </Grid>
      </CardContent>
    </StyledCard>
  );
};

export default ListCard;
